import { useMemo } from "react";
import { Link, NavLink, Navigate, useParams } from "react-router-dom";
import { marked } from "marked";
import { useMeta, usePageMeta } from "../../seo";

import api from "../../content/developers/api.md?raw";
import customerLookup from "../../content/developers/customer-lookup.md?raw";
import ticketActions from "../../content/developers/ticket-actions.md?raw";
import webhooks from "../../content/developers/webhooks.md?raw";

// The developer docs are Markdown files in src/content/developers, so the
// text lives in one place and reads fine on GitHub too. Every .md file there
// is also listed in sitemap.xml (scripts/gen-sitemap.mjs), so add new docs to
// this list as well.
export const DOCS = [
  {
    slug: "api",
    title: "API",
    summary: "Read and create tickets with an API key; scopes, filters and examples.",
    body: api,
  },
  {
    slug: "webhooks",
    title: "Webhooks",
    summary: "Get a signed POST when tickets are created, updated or solved.",
    body: webhooks,
  },
  {
    slug: "customer-lookup",
    title: "Customer data lookup",
    summary: "Show details from your own system next to each ticket.",
    body: customerLookup,
  },
  {
    slug: "ticket-actions",
    title: "Ticket actions",
    summary: "Buttons on tickets that hand them to your own systems, like Create lead.",
    body: ticketActions,
  },
];

export function DevelopersIndex() {
  usePageMeta("/developers");
  return (
    <DocsShell>
      <h1 className="text-3xl font-semibold tracking-tight text-ink-900">Build on ZopDesk</h1>
      <p className="mt-3 max-w-2xl text-ink-600">
        Connect ZopDesk to the rest of your stack: read and create tickets from your own tools, hear about changes
        as they happen, and show your own customer data to agents on every ticket.
      </p>
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {DOCS.map((d) => (
          <Link
            key={d.slug}
            to={`/developers/${d.slug}`}
            className="rounded-xl border border-ink-200 bg-white p-5 shadow-sm transition hover:border-brand-300 hover:shadow"
          >
            <div className="font-semibold text-ink-900">{d.title}</div>
            <p className="mt-1 text-sm text-ink-600">{d.summary}</p>
          </Link>
        ))}
      </div>
    </DocsShell>
  );
}

export function DeveloperDoc() {
  const { slug } = useParams();
  const doc = DOCS.find((d) => d.slug === slug);
  const html = useMemo(() => (doc ? (marked.parse(doc.body, { async: false }) as string) : ""), [doc]);

  useMeta({
    path: `/developers/${slug ?? ""}`,
    title: doc ? `${doc.title} · ZopDesk developers` : "Developers · ZopDesk",
    description: doc?.summary ?? "",
  });

  if (!doc) return <Navigate to="/developers" replace />;
  return (
    <DocsShell>
      {/* Our own Markdown from the repo, not user input. */}
      <article className="doc" dangerouslySetInnerHTML={{ __html: html }} />
    </DocsShell>
  );
}

function DocsShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-10 sm:px-6 md:flex-row md:py-14">
      <nav className="flex-none md:w-52">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-500">Developers</div>
        <ul className="flex flex-wrap gap-1 md:flex-col">
          <li>
            <NavLink
              to="/developers"
              end
              className={({ isActive }) =>
                `block rounded-md px-2.5 py-1.5 text-sm ${isActive ? "bg-brand-50 font-semibold text-brand-800" : "text-ink-700 hover:bg-ink-100"}`
              }
            >
              Overview
            </NavLink>
          </li>
          {DOCS.map((d) => (
            <li key={d.slug}>
              <NavLink
                to={`/developers/${d.slug}`}
                className={({ isActive }) =>
                  `block rounded-md px-2.5 py-1.5 text-sm ${isActive ? "bg-brand-50 font-semibold text-brand-800" : "text-ink-700 hover:bg-ink-100"}`
                }
              >
                {d.title}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
